import {
  Button,
  Col,
  DatePicker,
  Input,
  Row,
  Table,
  Tooltip,
} from "antd"
import { useCallback, useMemo, useState } from "react"
import { useMemberId } from "pages/member"
import styles from "./styles.module.scss"
import { useTranslation } from "react-i18next"
import { ReactComponent as ShowIcon } from "assets/icons/show.svg"
import { useNavigate, useSearchParams } from "react-router-dom"
import { formatDay } from "utils/format-datetime"
import { IEvent } from "../types"
import { useSubscriptionsHistory } from "./queries"
import type { Dayjs } from "dayjs"
import { RangePickerProps } from "antd/es/date-picker"
import dayjs from "dayjs"

const { Search } = Input
const { RangePicker } = DatePicker

const ACTIVITY_PARAM = "q"

const getYearMonth = (date: Dayjs) => date.year() * 12 + date.month()

const History = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const memberId = useMemberId()

  const [searchParams, setSearchParams] = useSearchParams()
  const [monthRange, setMonthRange] = useState<RangePickerProps["value"]>()

  const memberEvents = useSubscriptionsHistory(
    memberId!,
    monthRange?.[0] && monthRange?.[1]
      ? {
          start_date: dayjs(monthRange?.[0]).startOf('month').format("YYYY-MM-DD"),
          end_date: dayjs(monthRange?.[1]).endOf("month").format("YYYY-MM-DD"),
        }
      : undefined
  )

  const filteredHistory = useMemo(() => {
    if (searchParams.get(ACTIVITY_PARAM)) {
      return memberEvents.data
        ?.filter(
          (element) =>
            element.name
              .toLowerCase()
              .startsWith(searchParams.get(ACTIVITY_PARAM)!.toLowerCase()) &&
            element
        )
        .sort((a, b) => a.name.localeCompare(b.name))
    }
    return memberEvents.data || []
  }, [memberEvents.data, searchParams])

  const disabled6MonthsDate: any = useCallback(
    (current: Dayjs) => {
      const from = monthRange?.[0]
      const to = monthRange?.[1]
      if (from) {
        const minDate = from.add(0, "months")
        const maxDate = from.add(5, "months")

        return (
          getYearMonth(current) < getYearMonth(minDate) ||
          getYearMonth(current) > getYearMonth(maxDate)
        )
      }
      if (to && !from) {
        const minDate = to.add(-5, "months")
        const maxDate = to.add(0, "months")

        return (
          getYearMonth(current) < getYearMonth(minDate) ||
          getYearMonth(current) > getYearMonth(maxDate)
        )
      }

      return false
    },
    [monthRange]
  )

  return (
    <>
      <Row style={{ marginBottom: 15 }}>
        <Col xs={24} md={8}>
          <RangePicker
            picker="month"
            disabledDate={disabled6MonthsDate}
            value={monthRange}
            onCalendarChange={(dates) => {
              setMonthRange(dates)
            }}
          />
        </Col>
        <Col md={{ span: 8, offset: 8 }} xs={24}>
          <Search
            value={searchParams.get(ACTIVITY_PARAM) || ""}
            onChange={(e) => {
              const newValue = e.target.value
              newValue !== ""
                ? searchParams.set(ACTIVITY_PARAM, newValue)
                : searchParams.delete(ACTIVITY_PARAM)
              setSearchParams(searchParams, { replace: true })
            }}
            placeholder={t("global.search")}
            style={{ width: "100%" }}
          />
        </Col>
      </Row>
        <Table<IEvent>
          tableLayout="fixed"
          className={styles.table}
          pagination={false}
          loading={memberEvents.isLoading || memberEvents.isPreviousData}
          rowKey={"id"}
          columns={[
            {
              title: t<string>("events.activities"),
              dataIndex: "name",
              key: "activities",
              ellipsis: true,
              render: (value) => (
                <Tooltip placement="bottomLeft" title={value}>
                  {value}
                </Tooltip>
              ),
            },
            {
              title: t<string>("events.title"),
              dataIndex: "start_date",
              key: "events",
              ellipsis: true,
              render: (text, record) => {
                const value = `${formatDay(record.start_date)}, ${
                  record.start_hour
                }h - ${record.end_hour}h`
                return (
                  <Tooltip placement="bottomLeft" title={value}>
                    {value}
                  </Tooltip>
                )
              },
            },
            {
              title: t<string>("global.actions"),
              key: "action",
              width: 100,
              render: (_, record) => (
                <>
                  <Tooltip placement="bottom" title={t("global.show")}>
                    <Button
                      type="text"
                      shape="circle"
                      onClick={() => navigate(`../show/${record.id}`)}
                      icon={<ShowIcon />}
                    />
                  </Tooltip>
                  {/* <Tooltip
                                placement="bottom"
                                title={t("events.unsubscribe")}
                              >
                                <Button
                                  type="text"
                                  shape="circle"
                                  disabled={
                                    !["draft", "approved"].includes(
                                      record.status!
                                    )
                                  }
                                  onClick={() => unsubscribeModal.open(record)}
                                  icon={<LeaveIcon />}
                                />
                              </Tooltip> */}
                </>
              ),
            },
          ]}
          dataSource={filteredHistory}
        />
    </>
  )
}

export default History
